import { useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Navigation } from "lucide-react";
import { cn } from "@/lib/utils";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";

import d1 from "@/assets/direction-1.webp";
import d2 from "@/assets/direction-2.webp";
import d3 from "@/assets/direction-3.webp";
import s4 from "@/assets/directions/ingresso-tigre.webp";
import s5 from "@/assets/directions/banco-macelleria.webp";

type Step = { image: string; label: string; caption: string };

const STEPS: Step[] = [
  { image: d1, label: "Come arrivare in Via J. F. Kennedy", caption: "Puoi raggiungerci da Viale Principe di Piemonte oppure da Viale Monsignor Secondo Bologna." },
  { image: d2, label: "Imbocca Via J. F. Kennedy", caption: "Segui le indicazioni fino a immetterti in Via John Fitzgerald Kennedy." },
  { image: d3, label: "Parcheggio Tigre", caption: "Parcheggia nell'area del supermercato Tigre al civico 1." },
  { image: s4, label: "Ingresso del Tigre", caption: "Entra dalla porta automatica del supermercato Tigre, in fondo al piazzale." },
  { image: s5, label: "La Bottega delle Carni da Marco", caption: "Sei arrivato! Cerca il banco Macelleria: ti aspettiamo lì." },
];

export function DirectionsCarousel() {
  const [i, setI] = useState(0);
  const total = STEPS.length;
  const step = STEPS[i];
  const isLast = i === total - 1;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Eyebrow>Come raggiungerci</Eyebrow>
        <SectionTitle className="mt-3" align="center">Dal parcheggio al nostro banco</SectionTitle>
        <p className="text-brand-warm-gray mt-3 max-w-2xl mx-auto font-italic-serif italic">
          Segui il percorso passo passo: dall'esterno del Tigre fino al banco della Bottega.
        </p>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-1.5 max-w-3xl mx-auto">
        {STEPS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            aria-label={`Vai al passo ${idx + 1}`}
            className={cn(
              "flex-1 h-1.5 rounded-full transition-colors",
              idx <= i ? "bg-brand-red" : "bg-brand-charcoal/15"
            )}
          />
        ))}
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-3 justify-center">
        <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-brand-red/10 text-brand-red font-display font-bold text-sm shrink-0">
          {isLast ? <MapPin className="h-4 w-4" /> : i + 1}
        </span>
        <span className="font-display text-sm font-bold text-brand-charcoal">
          {isLast ? "ARRIVO" : `Passo ${i + 1} di ${total - 1}`}
          <span className="font-body font-normal text-brand-warm-gray ml-2">— {step.label}</span>
        </span>
      </div>

      {/* Image */}
      <div className="relative rounded-md overflow-hidden shadow-[var(--shadow-soft)] bg-brand-cream-dark max-w-5xl mx-auto">
        <div className="aspect-[16/7] relative">
          <img
            key={i}
            src={step.image}
            alt={step.label}
            width={1280}
            height={560}
            loading={i === 0 ? "eager" : "lazy"}
            className="h-full w-full object-cover animate-in fade-in duration-500"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5 text-white">
            <div className="font-display text-base md:text-lg">{step.label}</div>
            <p className="text-xs md:text-sm text-white/85 mt-0.5">{step.caption}</p>
          </div>
        </div>

        <button
          onClick={() => setI(Math.max(0, i - 1))}
          disabled={i === 0}
          aria-label="Passo precedente"
          className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md disabled:opacity-30 hover:bg-white transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-brand-charcoal" />
        </button>
        <button
          onClick={() => setI(Math.min(total - 1, i + 1))}
          disabled={isLast}
          aria-label="Passo successivo"
          className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md disabled:opacity-30 hover:bg-white transition-colors"
        >
          <ChevronRight className="h-5 w-5 text-brand-charcoal" />
        </button>
      </div>

      {/* Destination card */}
      <div className="max-w-5xl mx-auto flex items-center gap-4 p-4 rounded-md border border-brand-charcoal/10 bg-white">
        <div className="h-16 w-24 rounded-md overflow-hidden shrink-0">
          <img src={s5} alt="Il banco della Bottega delle Carni da Marco" className="h-full w-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-sm font-bold text-brand-charcoal">Destinazione: La Bottega delle Carni da Marco</p>
          <p className="text-xs text-brand-warm-gray">Via J. F. Kennedy 1 — 86100 Campobasso (CB)</p>
        </div>
        <a
          href="https://www.google.com/maps?q=Via+J.+F.+Kennedy+1,+Campobasso"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 bg-brand-red hover:bg-brand-red-hover text-white text-xs uppercase tracking-[0.16em] px-3 py-2 rounded-md shrink-0"
        >
          <Navigation size={14} /> <span className="hidden sm:inline">Apri in Maps</span>
        </a>
      </div>
    </div>
  );
}

export default DirectionsCarousel;